import { useState } from 'react'
import { getTokenLogoUri } from '../../utils/tokenMeta'

interface Props {
  /** Token symbol (e.g. "SOL") or raw mint address. */
  symbol: string
  /** Explicit mint address — preferred over symbol for icon lookup when available. */
  mint?: string
  /** Icon diameter in pixels. Default 20. */
  size?: number
  className?: string
}

/**
 * Renders a circular token icon sourced from the Jupiter image CDN.
 * Falls back to a coloured circle with the first letter(s) of the symbol
 * when the image cannot be loaded (404, network error, unknown token).
 */
export default function TokenIcon({ symbol, mint, size = 20, className = '' }: Props) {
  const [failed, setFailed] = useState(false)
  const logoUri = getTokenLogoUri(mint ?? symbol)

  const initials = symbol
    .replace(/[^a-zA-Z]/g, '')
    .slice(0, 2)
    .toUpperCase() || '?'

  const style = { width: size, height: size, minWidth: size, fontSize: size * 0.42 }

  if (!logoUri || failed) {
    return (
      <span
        className={`inline-flex items-center justify-center rounded-full font-bold text-ink select-none ${pickColor(symbol)} ${className}`}
        style={style}
        title={symbol}
      >
        {initials[0]}
      </span>
    )
  }

  return (
    <img
      src={logoUri}
      alt={symbol}
      title={symbol}
      crossOrigin="anonymous"
      className={`rounded-full object-cover ${className}`}
      style={style}
      onError={() => setFailed(true)}
    />
  )
}

/** Deterministic background colour based on symbol string. */
function pickColor(symbol: string): string {
  const colours = [
    'bg-accent-blue',
    'bg-accent-purple',
    'bg-cyan-500',
    'bg-emerald-500',
    'bg-orange-500',
    'bg-pink-500',
    'bg-yellow-500',
    'bg-indigo-500',
  ]
  let hash = 0
  for (let i = 0; i < symbol.length; i++) hash = (hash * 31 + symbol.charCodeAt(i)) >>> 0
  return colours[hash % colours.length]
}
